/**
 * Mock File Storage Adapter
 * Implements IFileStoragePort with mock data for development/testing
 */

import { MOCK_COBOL_FILE, MOCK_PROJECT_FILES, MOCK_COBOL_FILES_CONTENT, MOCK_JAVA_FILES_CONTENT, MOCK_JAVA_SKELETON } from '../../data/mockCobolFiles'

// In-memory file store
const fileStore = {
  'app-1': {
    '/cobol': {
      type: 'directory',
      children: ['LGACUS01.cbl', 'LGACDB01.cbl', 'LGICUS01.cbl'],
    },
    '/cobol/LGACUS01.cbl': {
      type: 'file',
      content: MOCK_COBOL_FILES_CONTENT['lgacus01'],
      size: MOCK_COBOL_FILES_CONTENT['lgacus01'].length,
    },
    '/cobol/LGACDB01.cbl': {
      type: 'file',
      content: MOCK_COBOL_FILES_CONTENT['lgacdb01'],
      size: MOCK_COBOL_FILES_CONTENT['lgacdb01'].length,
    },
    '/cobol/LGICUS01.cbl': {
      type: 'file',
      content: MOCK_COBOL_FILES_CONTENT['lgicus01'],
      size: MOCK_COBOL_FILES_CONTENT['lgicus01'].length,
    },
    '/java': {
      type: 'directory',
      children: ['CustomerService.java', 'CustomerRepository.java', 'CustomerController.java'],
    },
    '/java/CustomerService.java': {
      type: 'file',
      content: MOCK_JAVA_FILES_CONTENT['customerservice'],
      size: MOCK_JAVA_FILES_CONTENT['customerservice'].length,
    },
    '/java/CustomerRepository.java': {
      type: 'file',
      content: MOCK_JAVA_FILES_CONTENT['customerrepository'],
      size: MOCK_JAVA_FILES_CONTENT['customerrepository'].length,
    },
    '/java/CustomerController.java': {
      type: 'file',
      content: MOCK_JAVA_FILES_CONTENT['customercontroller'],
      size: MOCK_JAVA_FILES_CONTENT['customercontroller'].length,
    },
  },
}

// Helper to simulate async delay
const delay = (ms = 50) => new Promise(resolve => setTimeout(resolve, ms))

export class MockFileStorageAdapter {
  async listFiles(applicationId, path = '/') {
    await delay()

    const appFiles = fileStore[applicationId] || {}
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const entry = appFiles[normalizedPath]

    if (!entry) {
      return []
    }

    if (entry.type === 'directory') {
      return entry.children.map(name => {
        const childPath = `${normalizedPath}/${name}`.replace('//', '/')
        const child = appFiles[childPath]
        return {
          name,
          path: childPath,
          isDirectory: child?.type === 'directory',
          size: child?.size || 0,
        }
      })
    }

    return []
  }

  async readFile(applicationId, path) {
    await delay()

    const appFiles = fileStore[applicationId] || {}
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const entry = appFiles[normalizedPath]

    if (!entry || entry.type !== 'file') {
      throw new Error(`File not found: ${path}`)
    }

    return entry.content
  }

  async writeFile(applicationId, path, content) {
    await delay()

    if (!fileStore[applicationId]) {
      fileStore[applicationId] = {}
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    fileStore[applicationId][normalizedPath] = {
      type: 'file',
      content,
      size: content.length,
    }

    return { path: normalizedPath, size: content.length }
  }

  async deleteFile(applicationId, path) {
    await delay()

    const appFiles = fileStore[applicationId]
    if (appFiles) {
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      delete appFiles[normalizedPath]
    }
  }

  async fileExists(applicationId, path) {
    await delay()

    const appFiles = fileStore[applicationId] || {}
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return !!appFiles[normalizedPath]
  }

  async uploadFiles(applicationId, files, destinationPath = '/') {
    await delay()

    if (!fileStore[applicationId]) {
      fileStore[applicationId] = {}
    }

    const results = []
    for (const file of files) {
      const filePath = `${destinationPath}/${file.name}`.replace('//', '/')

      // Handle both File objects and {name, content} objects
      const content = file.content || await file.text?.() || ''

      fileStore[applicationId][filePath] = {
        type: 'file',
        content,
        size: content.length,
      }

      results.push({
        name: file.name,
        path: filePath,
        size: content.length,
      })
    }

    return results
  }

  async uploadAndExtractZip(applicationId, zipFile, destinationPath = '/') {
    await delay(200) // Simulate longer upload/extract time

    // Mock extraction - in reality would use a zip library
    return {
      extractedFiles: [
        { name: 'PROGRAM1.cbl', path: `${destinationPath}/PROGRAM1.cbl` },
        { name: 'PROGRAM2.cbl', path: `${destinationPath}/PROGRAM2.cbl` },
      ],
      totalFiles: 2,
    }
  }

  async createDirectory(applicationId, path) {
    await delay()

    if (!fileStore[applicationId]) {
      fileStore[applicationId] = {}
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    fileStore[applicationId][normalizedPath] = {
      type: 'directory',
      children: [],
    }
  }

  async deleteDirectory(applicationId, path) {
    await delay()

    const appFiles = fileStore[applicationId]
    if (appFiles) {
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      // Delete directory and all children
      for (const key of Object.keys(appFiles)) {
        if (key.startsWith(normalizedPath)) {
          delete appFiles[key]
        }
      }
    }
  }

  async getDownloadUrl(applicationId, path) {
    await delay()

    // For mock, return content directly (no URL)
    const content = await this.readFile(applicationId, path)
    return { content }
  }

  async downloadAsZip(applicationId, paths) {
    await delay(100)

    // Mock - in reality would create actual zip
    return new Blob(['mock zip content'], { type: 'application/zip' })
  }

  async getStoragePath(applicationId) {
    await delay()
    return `mock://storage/${applicationId}`
  }

  async getStorageUsage(applicationId) {
    await delay()

    const appFiles = fileStore[applicationId] || {}
    let totalFiles = 0
    let totalSize = 0

    for (const entry of Object.values(appFiles)) {
      if (entry.type === 'file') {
        totalFiles++
        totalSize += entry.size || 0
      }
    }

    return { totalFiles, totalSize }
  }

  // Additional helper methods for the UI

  /**
   * Get project files structure (matches current mock data format)
   */
  async getProjectFiles(applicationId) {
    await delay()
    return MOCK_PROJECT_FILES
  }

  /**
   * Get COBOL file with sections (matches current mock data format)
   */
  async getCobolFileWithSections(applicationId, fileId) {
    await delay()
    if (fileId === 'lgacus01') {
      return MOCK_COBOL_FILE
    }
    // Return basic structure for other files
    return {
      name: `${fileId.toUpperCase()}.cbl`,
      content: MOCK_COBOL_FILES_CONTENT[fileId] || '',
      sections: [],
    }
  }

  /**
   * Get Java skeleton (matches current mock data format)
   */
  async getJavaSkeleton(applicationId) {
    await delay()
    return MOCK_JAVA_SKELETON
  }
}

// Export singleton instance
export const mockFileStorageAdapter = new MockFileStorageAdapter()
